import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";

interface Props {
  onClose: () => void;
  onApply: (ids: string[]) => void;
  mode: "beam" | "node";
  loadName: string;
  existingIds?: string[];
}

export default function ManualLoadInput({
  onClose,
  onApply,
  mode,
  loadName,
  existingIds = [],
}: Props) {
  const [inputText, setInputText] = useState(existingIds.join(", "));
  const [error, setError] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Update input when existingIds change
  useEffect(() => {
    setInputText(existingIds.join(", "));
  }, [existingIds]);

  // Center dialog on open
  useEffect(() => {
    if (dialogRef.current) {
      const dialogWidth = 500;
      const dialogHeight = dialogRef.current.offsetHeight || 500;
      setPosition({
        x: Math.max(0, (window.innerWidth - dialogWidth) / 2),
        y: Math.max(0, (window.innerHeight - dialogHeight) / 2),
      });
    }
  }, []);

  // -------- Draggable Header ----------
  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.current.x,
          y: e.clientY - dragStart.current.y,
        });
      }
    };
    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // -------- Parsing Logic ----------
  const expandIds = () => {
    try {
      const parts = inputText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const output: string[] = [];

      parts.forEach((p) => {
        if (p.includes("-")) {
          const [start, end] = p.split("-").map((v) => Number(v));
          if (isNaN(start) || isNaN(end) || start > end)
            throw new Error("Invalid range");

          for (let i = start; i <= end; i++) output.push(String(i));
        } else {
          if (isNaN(Number(p))) throw new Error("Invalid number");
          output.push(String(Number(p)));
        }
      });

      setError("");
      return output;
    } catch (err: any) {
      setError("Invalid input format");
      return [];
    }
  };

  const handleApply = () => {
    const ids = expandIds();
    if (ids.length === 0) return;
    onApply(ids);
    onClose();
  };

  const handleClose = () => {
    setInputText(existingIds.join(", "));
    setError("");
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[500px] z-50"
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
      >
        {/* Header */}
        <div
          className="bg-blue-600 text-white px-4 py-3 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex justify-between items-center"
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-lg font-semibold">
            Enter {mode === "beam" ? "Beam" : "Node"} IDs
          </h2>
          <button onClick={handleClose}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Load:{" "}
            <span className="font-semibold text-blue-600">{loadName}</span>
          </p>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Enter IDs separated by commas. You can also use ranges:
          </p>

          <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded text-xs font-mono space-y-1 mb-4">
            <div>1, 2, 3, 5</div>
            <div>1-5, 8, 10-12</div>
            <div>1, 3-7, 10, 15-20</div>
          </div>

          {/* Textbox */}
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="e.g., 1, 3-7, 10, 20-25"
            className="w-full px-3 py-2 border border-gray-300 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            rows={4}
          />

          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-600">
              {error}
            </div>
          )}

          {/* Preview section */}
          {inputText && !error && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview:
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {inputText}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!inputText.trim()}
            className={`px-4 py-2 text-sm font-medium text-white rounded transition-colors ${
              inputText.trim()
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {mode === "beam" ? "Apply to Beams" : "Apply to Nodes"}
          </button>
        </div>
      </div>
    </>
  );
}
